import { Badge, Container } from "react-bootstrap";
import AccordionComponent from "../../../../common/Accordion";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";
import { GetAppointmentList } from "../../../../services/AppointmentListApi";
import SearchInput from "../../../../common/InlineSearch";
import DataTable from "../../../../common/DataTable";
import { APPOINTMENT_LIST_HEADERS } from "../actions/constant";
import { QME_APPOINTMENT_STATUS } from "../../../../common/Constant";
import Snackbar from "../../../../common/Snackbar";
import { handleGeneratePDF } from "../../Case_Records/components/110Pdf";
import {
  downloadQMEFormsZip,
  downloadSIBTFForm,
} from "../../../../services/AppointmentsAPI";

interface AppointmentProps {
  userData: any;
  action: string;
  loggedInUserid: any;
  caseStageId: any;
  qmeActiveTab: any;
  onCancel: (action: string) => void;
  onRowSelect: (selectedRowData: any) => void;
}

const AppointmentList: React.FC<AppointmentProps> = ({
  userData,
  action,
  loggedInUserid,
  caseStageId,
  qmeActiveTab,
  onCancel,
  onRowSelect,
}) => {
  const [activeKeys, setActiveKeys] = useState<string[]>(["today"]);
  const caseInfoID = useSelector(
    (state: RootState) => state.caseHeader.caseInfoId
  );
  const [data, setData] = useState<any[]>([]);
  const searchInputRef = useRef<any>(null);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchQuerylength, setSearchQuerylength] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showMessagePopUp, setShowMessagePopUp] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const CaseTypeName = useSelector(
    (state: RootState) => state.caseHeader.CaseTypeName
  );
  
  const fetchAppointmentData = async (query: string = "") => {
    const appointmentData = await GetAppointmentList(caseInfoID, query);
    if (appointmentData) {
      const formattedData = appointmentData.map((item: any) => ({
        ...item,
        download: item.subjectQmeScheduleId,
        startTime: formatDateTime(item.startTime), // Format startTime here
      }));

      setData(formattedData);

      setFilteredData(formattedData);

      const hasToday = formattedData.some(
        (item: any) => item.scheduleCategory === "Today"
      );
      const hasUpcoming = formattedData.some(
        (item: any) => item.scheduleCategory === "Upcoming"
      );
      const hasPast = formattedData.some(
        (item: any) => item.scheduleCategory === "Past"
      );

      if (query.trim() === "") {
        if (hasToday) {
          setActiveKeys(["0"]);
        } else if (hasUpcoming) {
          setActiveKeys(["1"]);
        } else if (hasPast) {
          setActiveKeys(["2"]);
        } else {
          setActiveKeys([]); // Nothing to open if no data at all
        }
      }
    }
  };

  useEffect(() => {
    if (qmeActiveTab == "Appointment List") {
      fetchAppointmentData();
    }
  }, [qmeActiveTab]);

  const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    const formattedDate = date.toLocaleDateString("en-US");
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${formattedDate} ${formattedTime}`;
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchAppointmentData(searchQuery);
    }, 300); // 300ms debounce delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // useEffect(() => {
  //   setSearchQuery("");
  //   searchInputRef.current?.clear(); // Clear input field visually
  // }, []);

  const handleReset = () => {
    searchInputRef.current?.clear();
    setSearchQuery("");
    setSearchQuerylength("");
    fetchAppointmentData();
  };

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);

    // Re-filter data based on search query
    const newFilteredData = data.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(query.toLowerCase())
      )
    );

    setFilteredData(newFilteredData);

    // If search query is empty, reset filteredData to full data set
    if (query.trim() === "") {
      setFilteredData(data);
    }

    // Extract dynamically assigned event keys from the rendered accordions
    const allKeys = ["today", "0", "1", "2"]; // Use the correct event keys
    if (query.trim() !== "") {
      setActiveKeys(allKeys); // Open all accordions
    } else {
      setActiveKeys(["today"]); // Reset when cleared
    }
  };

  const handleSearchQueryChangeForlength = (query: string) => {
    setSearchQuerylength(query?.trim());
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case QME_APPOINTMENT_STATUS.SCHEDULED:
        return "text-primary"; // Bootstrap blue
      case QME_APPOINTMENT_STATUS.CANCELLED:
        return "text-danger"; // Bootstrap red
      case QME_APPOINTMENT_STATUS.COMPLETED:
        return "text-success"; // Bootstrap red
      default:
        return "text-secondary"; // Default black
    }
  };

  const formatRows = (data: any[]) =>
    data.map((item, index) => ({
      ...item,
      srNo: index + 1,

      appointmentStatusDesc: (
        <span className={getStatusColor(item.appointmentStatusDesc)}>
          {item.appointmentStatusDesc}
        </span>
      ),
      status: item.appointmentStatusDesc,
    }));

  const renderAccordion = (
    items: { key: string; title: string; count: number; content: JSX.Element }[]
  ) => (
    <AccordionComponent
      items={items.map(({ key, title, count, content }) => ({
        title: (
          <>
            {title}{" "}
            <span className="primary-badge badge rounded-pill bg-primary ms-2">
              {count}
            </span>
          </>
        ),
        content,
        eventKey: key,
      }))}
      activeKey={activeKeys}
      onSelect={(key) => {
        setActiveKeys(
          (prevKeys) =>
            prevKeys.includes(key as string)
              ? prevKeys.filter((k) => k !== key) // If already open, close it
              : [key as string] // Otherwise, open it and close others
        );
      }}
    />
  );

  const [selectedRowData, setSelectedRowData] = useState(null);

  const handleRowAction = (row: any, action: string) => {
    setSelectedRowData(row);
    handleReset();
    onRowSelect(row);
  };
  const handleDownloadAction = (row: any) => {
    setSnackbarMessage("Generating 110/SIBTF Form");
    setError("success");
    setSnackbarOpen(true);
    if (CaseTypeName == "SIBTF (Subsequent Injuries Benefits Trust Fund)") {
      downloadSIBTFForm([row.subjectQmeScheduleId]);
    } else {
      downloadQMEFormsZip([row.subjectQmeScheduleId]);
    }
  };
  return (
    <div className="pb-5 position-relative">
      <div
        className="d-flex  py-2 search_closer align-items-center position-absolute justify-content-end"
        style={{ top: "-60px", right: "0px", width: "30%" }}
      >
        <Snackbar
          message={snackbarMessage}
          open={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
          severity={error}
        />
        {showMessagePopUp && (
          <div className="alert alert-info text-center position-fixed">
            Download is in progress...
          </div>
        )}
        <div style={{ width: "85%" }} className="gridsearch">
          <SearchInput
            ref={searchInputRef}
            data={data}
            searchKeys={[]}
            onSearchResult={setFilteredData}
            placeholder="Search"
            onSearchQueryChange={handleSearchQueryChange}
            onInputChange={handleSearchQueryChangeForlength}
          />
        </div>
        <button
          onClick={handleReset}
          className="reset_btn"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            marginTop: "3px",
          }}
        >
          <span className="material-icons reset_ic0">restart_alt</span>
        </button>
      </div>
      {renderAccordion([
        {
          key: "today",
          title: "Today's Appointment",
          count: data[0]?.totalAppointmentsToday || 0,
          content: (
            <DataTable
              headers={APPOINTMENT_LIST_HEADERS}
              rows={formatRows(
                filteredData.filter((item) => item.scheduleCategory === "Today")
              )}
              onAction={handleRowAction}
              isTodayAppointmentTable={true}
              onDownloadAction={handleDownloadAction}
            />
          ),
        },
        {
          key: "upcoming",
          title: "Upcoming Appointments",
          count: data[0]?.totalAppointmentsUpcoming || 0,
          content: (
            <DataTable
              headers={APPOINTMENT_LIST_HEADERS}
              rows={formatRows(
                filteredData.filter(
                  (item) => item.scheduleCategory === "Upcoming"
                )
              )}
              onAction={handleRowAction}
              onDownloadAction={handleDownloadAction}
              isUpcomingAppointmentTable={true}
            />
          ),
        },
        {
          key: "past",
          title: "Past Appointments",
          count: data[0]?.totalAppointmentsPast || 0,
          content: (
            <DataTable
              headers={APPOINTMENT_LIST_HEADERS}
              rows={formatRows(
                filteredData.filter((item) => item.scheduleCategory === "Past")
              )}
              onAction={handleRowAction}
              onDownloadAction={handleDownloadAction}
              isPastAppointmentTable={true}
            />
          ),
        },
      ])}
    </div>
  );
};

export default AppointmentList;
